import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { http, formatApiError } from "@/lib/api";
import { PageHeader, Panel } from "@/components/admin/PageBits";
import { BlocksTable } from "@/components/admin/BlocksTable";

const ToggleRow = ({ title, desc, checked, onChange, testId }) => (
  <label className="flex cursor-pointer items-start justify-between gap-6 rounded-md border border-white/10 p-4">
    <span>
      <span className="block text-sm font-medium text-slate-100">{title}</span>
      <span className="mt-1 block text-xs leading-relaxed text-slate-400">{desc}</span>
    </span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

const IpList = ({ label, hint, value, onChange, testId }) => (
  <div>
    <p className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
    <p className="mb-2 text-xs text-slate-500">{hint}</p>
    <Textarea rows={5} value={value} onChange={(e) => onChange(e.target.value)} placeholder={"Ex.: 177.10.20.30\nEx.: 200.150.0.0/16"}
      className="border-white/10 bg-black/30 font-mono-x text-sm text-slate-100 placeholder:text-slate-600" data-testid={testId} />
  </div>
);

export default function SecurityPage() {
  const [s, setS] = useState(null);
  const [me, setMe] = useState(null);
  const [allow, setAllow] = useState("");
  const [block, setBlock] = useState("");

  const load = useCallback(() => http.get("/admin/settings").then(({ data }) => {
    setS(data); setAllow((data.ip_allowlist || []).join("\n")); setBlock((data.ip_blocklist || []).join("\n"));
  }).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); http.get("/admin/security/me").then(({ data }) => setMe(data)).catch(() => {}); }, [load]);

  const save = async (patch, msg) => {
    try { const { data } = await http.put("/admin/settings", patch); setS(data); toast.success(msg); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const lines = (t) => t.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);

  if (!s) return null;
  return (
    <div className="space-y-6" data-testid="security-page">
      <PageHeader title="Segurança" subtitle="Bloqueie acessos de fora do Brasil e robôs. Vale apenas para o portal dos clientes; o painel não é afetado." />
      {me && <p className="text-sm text-slate-400" data-testid="security-my-ip">Seu IP atual: <span className="font-mono-x text-slate-200">{me.ip}</span> {me.country ? `(${me.country})` : ""}</p>}
      <Panel title="Proteções" testId="security-toggles">
        <div className="space-y-3">
          <ToggleRow title="Bloquear IPs de fora do Brasil" desc="Visitantes com IP de outro país veem a página de erro 500. Base de países DB-IP Lite."
            checked={s.block_foreign} onChange={(v) => save({ block_foreign: v }, v ? "Bloqueio de IPs estrangeiros ativado." : "Bloqueio de IPs estrangeiros desativado.")} testId="block-foreign-toggle" />
          <ToggleRow title="Bloquear robôs" desc="Recusa scripts, navegadores sem interface, robôs de busca e ferramentas de varredura. Inclui um campo invisível que só robôs preenchem."
            checked={s.bot_protection} onChange={(v) => save({ bot_protection: v }, v ? "Bloqueio de robôs ativado." : "Bloqueio de robôs desativado.")} testId="bot-protection-toggle" />
        </div>
      </Panel>
      <Panel title="Listas de IP" testId="security-lists">
        <div className="grid gap-6 md:grid-cols-2">
          <IpList label="Sempre liberados" hint="Um IP ou faixa por linha. Passam por todos os bloqueios." value={allow} onChange={setAllow} testId="ip-allowlist-input" />
          <IpList label="Sempre bloqueados" hint="Um IP ou faixa por linha. Bloqueados mesmo sendo do Brasil." value={block} onChange={setBlock} testId="ip-blocklist-input" />
        </div>
        <button onClick={() => save({ ip_allowlist: lines(allow), ip_blocklist: lines(block) }, "Listas de IP salvas.")} className="btn-gold mt-5 h-10 px-5 text-sm" data-testid="ip-lists-save-btn">Salvar listas</button>
      </Panel>
      <BlocksTable onBlockIp={(ip) => setBlock((b) => (b ? `${b}\n${ip}` : ip))} />
    </div>
  );
}
